import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  PageHeader,
  Spinner,
  EmptyState,
  Field,
  Tabs,
  Badge,
  ErrorBanner,
  formatCents,
} from '../components/ui';

interface Branding {
  brandName?: string;
  primaryColor?: string;
  logoUrl?: string;
  faviconUrl?: string;
  customCss?: string;
}

interface Domain {
  id: string;
  hostname: string;
  status: 'pending' | 'verified' | 'failed';
  sslStatus?: 'active' | 'pending';
}

interface Plan {
  id: string;
  name: string;
  priceCents: number;
  interval: 'month' | 'year';
}

interface Subscription {
  id: string;
  planId: string;
  status: 'active' | 'paused' | 'cancelled';
  startedAt: string;
}

type Tab = 'branding' | 'domains' | 'plans' | 'subscriptions' | 'usage' | 'reseller';

export function WhitelabelPage() {
  const [tab, setTab] = useState<Tab>('branding');
  return (
    <div>
      <PageHeader title="White-label" subtitle="Brand and resell the platform" />
      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'branding', label: 'Branding' },
          { value: 'domains', label: 'Domains' },
          { value: 'plans', label: 'Plans' },
          { value: 'subscriptions', label: 'Subscriptions' },
          { value: 'usage', label: 'Usage' },
          { value: 'reseller', label: 'Reseller' },
        ]}
      />
      {tab === 'branding' && <BrandingTab />}
      {tab === 'domains' && <DomainsTab />}
      {tab === 'plans' && <PlansTab />}
      {tab === 'subscriptions' && <SubsTab />}
      {tab === 'usage' && <UsageTab />}
      {tab === 'reseller' && <ResellerTab />}
    </div>
  );
}

function BrandingTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get<Branding>('/branding').catch((): Branding => ({})),
  });
  const initial = q.data;
  const [name, setName] = useState(initial?.brandName ?? '');
  const [color, setColor] = useState(initial?.primaryColor ?? '#3b82f6');
  const [logo, setLogo] = useState(initial?.logoUrl ?? '');
  const [css, setCss] = useState(initial?.customCss ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.patch('/branding', {
        brandName: name || undefined,
        primaryColor: color,
        logoUrl: logo || undefined,
        customCss: css || undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branding'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  if (q.isLoading) return <Spinner />;

  return (
    <Card>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-4)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <Field label="Brand name">
            <Input value={name} onChange={setName} />
          </Field>
          <Field label="Primary color">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: '100%', height: 36 }} />
          </Field>
          <Field label="Logo URL">
            <Input value={logo} onChange={setLogo} placeholder="https://…" />
          </Field>
          <Field label="Custom CSS">
            <textarea
              value={css}
              onChange={(e) => setCss(e.target.value)}
              rows={6}
              style={{
                padding: 'var(--space-2)',
                border: '1px solid var(--color-border-primary)',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'monospace',
                fontSize: 'var(--text-xs)',
              }}
            />
          </Field>
          <ErrorBanner error={error} />
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save branding'}
          </Button>
        </div>
        <Card style={{ background: 'var(--color-surface-secondary)', padding: 'var(--space-3)' }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>Preview</div>
          {logo && <img src={logo} alt="Logo" style={{ maxHeight: 40 }} />}
          <h3 style={{ color }}>{name || 'Your brand'}</h3>
          <Button style={{ background: color }}>Sample button</Button>
        </Card>
      </div>
    </Card>
  );
}

function DomainsTab() {
  const qc = useQueryClient();
  const [hostname, setHostname] = useState('');
  const q = useQuery({
    queryKey: ['custom-domains'],
    queryFn: () => api.get<{ data: Domain[] }>('/domains').catch(() => ({ data: [] })),
  });

  const addMut = useMutation({
    mutationFn: () => api.post('/domains', { hostname }),
    onSuccess: () => {
      setHostname('');
      qc.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Card>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Input value={hostname} onChange={setHostname} placeholder="app.example.com" />
          <Button onClick={() => hostname && addMut.mutate()} disabled={!hostname || addMut.isPending}>
            Add domain
          </Button>
        </div>
      </Card>
      {q.isLoading ? (
        <Spinner />
      ) : (q.data?.data ?? []).length === 0 ? (
        <Card><EmptyState title="No custom domains" /></Card>
      ) : (
        (q.data?.data ?? []).map((d) => (
          <Card key={d.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{d.hostname}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>SSL: {d.sslStatus ?? 'pending'}</div>
              </div>
              <Badge color={d.status === 'verified' ? 'green' : d.status === 'failed' ? 'red' : 'yellow'}>{d.status}</Badge>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

function PlansTab() {
  const q = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<{ data: Plan[] }>('/plans').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const plans = q.data?.data ?? [];
  if (plans.length === 0) return <Card><EmptyState title="No plans defined" /></Card>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--space-3)' }}>
      {plans.map((p) => (
        <Card key={p.id}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 700 }}>{p.name}</h3>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, marginTop: 'var(--space-2)' }}>
            {formatCents(p.priceCents)} <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>/{p.interval}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function SubsTab() {
  const q = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => api.get<Subscription | null>('/subscription').then((s) => ({ data: s ? [s] : [] })).catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const subs = q.data?.data ?? [];
  if (subs.length === 0) return <Card><EmptyState title="No subscriptions" /></Card>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {subs.map((s) => (
        <Card key={s.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{s.id}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>Plan {s.planId}</div>
            </div>
            <Badge color={s.status === 'active' ? 'green' : s.status === 'paused' ? 'yellow' : 'gray'}>{s.status}</Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}

function UsageTab() {
  const q = useQuery({
    queryKey: ['usage'],
    queryFn: () =>
      api
        .get<{ contacts: number; bookings: number; sentSms: number; sentEmails: number; storageBytes: number }>('/usage')
        .catch(() => ({ contacts: 0, bookings: 0, sentSms: 0, sentEmails: 0, storageBytes: 0 })),
  });
  if (q.isLoading) return <Spinner />;
  const u = q.data!;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
      <UsageStat label="Contacts" value={u.contacts.toLocaleString()} />
      <UsageStat label="Bookings" value={u.bookings.toLocaleString()} />
      <UsageStat label="SMS sent" value={u.sentSms.toLocaleString()} />
      <UsageStat label="Emails sent" value={u.sentEmails.toLocaleString()} />
      <UsageStat label="Storage" value={`${(u.storageBytes / (1024 * 1024)).toFixed(1)} MB`} />
    </div>
  );
}

function UsageStat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>{value}</div>
    </Card>
  );
}

function ResellerTab() {
  const q = useQuery({
    queryKey: ['reseller-tenants'],
    queryFn: () => api.get<{ data: { id: string; name: string; mrr: number }[] }>('/reseller/sub-tenants').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const tenants = q.data?.data ?? [];
  if (tenants.length === 0) return <Card><EmptyState title="No reseller tenants" description="Tenants you onboard will appear here with their MRR." /></Card>;
  return (
    <Card style={{ padding: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--color-surface-secondary)', textAlign: 'left' }}>
            <th style={cellStyle}>Tenant</th>
            <th style={cellStyle}>MRR</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
              <td style={cellStyle}>{t.name}</td>
              <td style={cellStyle}>{formatCents(t.mrr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  fontSize: 'var(--text-sm)',
};
